// Claude: MyRFXs.tsx — buyer's published discover requirements list; newest first 31-03-2026
// Claude: page-based detail view (no drawer); fine print; response count; tags popup 31-03-2026
import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Badge,
  Spinner,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Textarea,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import moment from "moment";
import { RfxAPI } from "../../Apis/rfxAPI";

interface MyRFXsProps {
  industryOptions?: { label: string; value: string }[];
  onEdit?: (rfx: any) => void;
  onSelectRfx?: (rfx: any) => void;
}

const MyRFXs = ({ industryOptions = [], onEdit, onSelectRfx }: MyRFXsProps) => {
  const [rfxs, setRfxs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [closingRfx, setClosingRfx] = useState<any | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [closeReason, setCloseReason] = useState("");
  const { isOpen: isCloseModalOpen, onOpen: onCloseModalOpen, onClose: onCloseModalClose } = useDisclosure();

  const load = async () => {
    setLoading(true);
    try {
      const res = await RfxAPI.getMyDiscoverRfxs();
      const items: any[] = Array.isArray(res?.data) ? res.data : [];
      items.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setRfxs(items);
    } catch {
      setRfxs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleConfirmClose = async () => {
    if (!closingRfx) return;
    setIsClosing(true);
    try {
      await RfxAPI.closeRfx(closingRfx._id, closeReason.trim());
      onCloseModalClose();
      setClosingRfx(null);
      setCloseReason("");
      load();
    } catch {
      // error handled by API
    } finally {
      setIsClosing(false);
    }
  };

  return (
    <Box>
      {loading && rfxs.length === 0 ? (
        <Flex justifyContent="center" py={10}>
          <Spinner size="sm" color="#0E122F" />
        </Flex>
      ) : rfxs.length === 0 ? (
        <Box textAlign="center" py={10}>
          <Text fontSize="sm" color="gray.400">No requirements published yet.</Text>
        </Box>
      ) : (
        <>
        <Text fontSize="10px" color="gray.400" mb={2} lineHeight="1.5">
          Click on the requirements below to see detailed responses. Solution providers/sellers can respond interest until expiry date.
        </Text>
        <Flex direction="column" gap={3}>
          {rfxs.map((rfx: any) => (
            <RequirementCard key={rfx._id} rfx={rfx} industryOptions={industryOptions}
              onClick={() => onSelectRfx?.(rfx)}
              onEdit={() => onEdit?.(rfx)}
              onClose={() => { setClosingRfx(rfx); onCloseModalOpen(); }}
            />
          ))}
        </Flex>
        </>
      )}

      {/* Close confirmation modal */}
      {/* Claude: close confirmation modal — closeReason required; shown to matched sellers 02-Apr-2026 */}
      <Modal isOpen={isCloseModalOpen} onClose={() => { onCloseModalClose(); setCloseReason(""); }} isCentered size="sm">
        <ModalOverlay />
        <ModalContent mx={4} borderRadius="16px">
          <ModalHeader fontSize="md" fontWeight={700}>Close Requirement?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {closingRfx && (
              <Box mb={3} p={3} bg="gray.50" borderRadius="8px" border="1px solid" borderColor="gray.200">
                <Text fontSize="xs" color="gray.500" noOfLines={2}>{closingRfx.rfxDetails}</Text>
              </Box>
            )}
            <Text fontSize="sm" color="gray.600" lineHeight="1.5">
              Closing this requirement will prevent any further responses. This cannot be undone.
            </Text>
            <Box mt={3}>
              <Textarea
                placeholder="Reason for closing"
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
                fontSize="sm" minH="70px" resize="none"
                borderRadius="8px" focusBorderColor="#0E122F"
              />
              <Text fontSize="10px" color="gray.400" mt={1}>Visible to matched sellers.</Text>
            </Box>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button size="sm" variant="outline" onClick={() => { onCloseModalClose(); setCloseReason(""); }} isDisabled={isClosing}>Cancel</Button>
            <Button size="sm" bg="#C62828" color="white" _hover={{ bg: "#9B0000" }}
              isLoading={isClosing} isDisabled={!closeReason.trim()} onClick={handleConfirmClose}>
              Yes, Close It
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

const RequirementCard = ({ rfx, industryOptions, onClick, onEdit, onClose }: {
  rfx: any;
  industryOptions: { label: string; value: string }[];
  onClick: () => void;
  onEdit: () => void;
  onClose: () => void;
}) => {
  // Claude: use rfx.status (MongoDB field, lowercase values) 01-Apr-2026
  // Claude: status label, edit/close guards — lowercase status values 01-Apr-2026
  const isExpired = rfx.status === "expired";
  const isClosed = rfx.status === "closed";
  const isInactive = isExpired || isClosed;
  const canEdit = rfx.status === "open";
  // Claude: republished is deemed closed — no close button 10-04-2026
  const canClose = rfx.status === "open";
  const STATUS_LABEL: Record<string, { label: string; color: string }> = {
    open:               { label: "Open",         color: "#2E7D32" },
    closed:             { label: "Closed",        color: "#757575" },
    expired:            { label: "Expired",       color: "#757575" },
    republished:        { label: "Republished",   color: "#6B46C1" },
    admin_review:       { label: "Under Review",  color: "#E65100" },
    admin_unpublished:  { label: "Unpublished",   color: "#C62828" },
  };
  const statusMeta = STATUS_LABEL[rfx.status] ?? { label: rfx.status, color: "#757575" };
  const expiryDate = rfx.expiryDate ? moment(rfx.expiryDate).format("DD MMM YYYY") : null;
  const shortId = rfx.rfxId ?? String(rfx._id).slice(-8);

  // Fine print — filters and tags
  const countries: string[] = rfx.targetCountry ?? [];
  const industries: string[] = rfx.targetIndustries ?? [];
  const tags: string[] = rfx.targetTags ?? [];
  const deptNames: string[] = (rfx.departments ?? []).map((d: any) => (typeof d === "object" ? d.name : d));
  const industryNames = industries.map((id: string) => {
    const match = industryOptions.find((o) => o.value === id);
    return match ? match.label : id;
  });
  const filterParts: string[] = [];
  if (countries.length > 0) filterParts.push(countries.join(", "));
  if (industryNames.length > 0) filterParts.push(industryNames.join(", "));
  if (deptNames.length > 0) filterParts.push(`Dept: ${deptNames.join(", ")}`);
  if (rfx.targetVerifiedOnly) filterParts.push("Verified only");
  const filterSummary = filterParts.join(" · ");

  const matchedCount: number = rfx.matchedCount ?? 0;

  return (
    <Box
      bg="white" borderRadius="12px" border="1px solid #D2D2D2" shadow="sm"
      overflow="hidden" cursor="pointer"
      _hover={{ borderColor: "#C9BFF5" }}
      onClick={onClick}
    >
      <Box px={4} pt={3} pb={3}>
        {/* Row 1: Description */}
        <Text fontSize="sm" color="gray.700" noOfLines={2} lineHeight="1.4" mb={2}>
          {rfx.rfxDetails}
        </Text>

        {/* Row 2: RFX ID · Status · Expiry · Responses · EDIT */}
        <Flex flexWrap="wrap" alignItems="center" columnGap="6px" rowGap="1px" mb={1}>
          <Text fontSize="9px" color="gray.400">{shortId}</Text>
          <Text fontSize="9px" color="gray.300">·</Text>
          <Text fontSize="9px" fontWeight={600} color={statusMeta.color}>
            Status:{statusMeta.label.toUpperCase()}{isClosed && rfx.closeReason ? ` (Reason: ${rfx.closeReason})` : ""}
          </Text>
          {!isClosed && (
            <>
              <Text fontSize="9px" color="gray.300">·</Text>
              <Text fontSize="9px" color={isExpired ? "gray.400" : "orange.500"}>
                {expiryDate ? (isExpired ? `Expired ${expiryDate}` : `Exp: ${expiryDate}`) : "No expiry"}
              </Text>
            </>
          )}
          {matchedCount > 0 && (
            <>
              <Text fontSize="9px" color="gray.300">·</Text>
              <Text fontSize="9px" color="#6B46C1" fontWeight={600}>
                {matchedCount} matched
              </Text>
            </>
          )}
          {canEdit && (
            <>
              <Text fontSize="9px" color="gray.300">·</Text>
              <Text fontSize="9px" color="#1565C0" fontWeight={600} cursor="pointer"
                _hover={{ textDecoration: "underline" }}
                onClick={(e) => { e.stopPropagation(); onEdit(); }}>
                EDIT
              </Text>
            </>
          )}
          {canClose && (
            <>
              <Text fontSize="9px" color="gray.300">·</Text>
              <Text fontSize="9px" color="#C62828" fontWeight={600} cursor="pointer"
                _hover={{ textDecoration: "underline" }}
                onClick={(e) => { e.stopPropagation(); onClose(); }}>
                CLOSE
              </Text>
            </>
          )}
        </Flex>

        {/* Row 3: Filters · Tags */}
        {(filterSummary || tags.length > 0) && (
          <Flex flexWrap="wrap" alignItems="center" columnGap="6px" rowGap="1px"
            maxH="26px" overflow="hidden">
            {filterSummary && (
              <Text fontSize="9px" color="gray.400" flexShrink={0}>{filterSummary}</Text>
            )}
            {filterSummary && tags.length > 0 && (
              <Text fontSize="9px" color="gray.300" flexShrink={0}>·</Text>
            )}
            {tags.length > 0 && <TagsFineprint tags={tags} />}
          </Flex>
        )}
      </Box>
    </Box>
  );
};

const MAX_INLINE_TAGS = 3;

const TagsFineprint = ({ tags }: { tags: string[] }) => {
  const inline = tags.slice(0, MAX_INLINE_TAGS).join(", ");
  const overflow = tags.length - MAX_INLINE_TAGS;

  return (
    <Flex alignItems="center" gap={1}>
      <Text fontSize="9px" color="gray.400" noOfLines={1}>
        Tags: {inline}
        {overflow <= 0 && ""}
      </Text>
      {overflow > 0 && (
        <Popover placement="top" isLazy>
          <PopoverTrigger>
            <Text
              as="span" fontSize="9px" color="#7C66FF" cursor="pointer" flexShrink={0}
              onClick={(e) => e.stopPropagation()}
              _hover={{ textDecoration: "underline" }}
            >
              +{overflow} more
            </Text>
          </PopoverTrigger>
          <PopoverContent w="220px" shadow="md" borderRadius="10px" onClick={(e) => e.stopPropagation()}>
            <PopoverCloseButton size="sm" />
            <PopoverBody pt={5} pb={3}>
              <Flex flexWrap="wrap" gap={1}>
                {tags.map((tag) => (
                  <Badge key={tag} fontSize="9px" px={2} py={0.5} borderRadius="full"
                    bg="gray.100" color="gray.600" textTransform="none">
                    {tag}
                  </Badge>
                ))}
              </Flex>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      )}
    </Flex>
  );
};

export default MyRFXs;
